import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TrustSignals = () => {
  const securityFeatures = [
    {
      icon: 'Shield',
      title: 'Enterprise Security',
      description: 'Bank-level encryption and security protocols'
    },
    {
      icon: 'Lock',
      title: 'GDPR Compliant',
      description: 'Full compliance with data protection regulations'
    },
    {
      icon: 'CheckCircle',
      title: 'SOC 2 Certified',
      description: 'Independently audited security controls'
    },
    {
      icon: 'Globe',
      title: 'Global Infrastructure',
      description: '99.9% uptime with worldwide data centers'
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: 'Sarah Johnson',
      role: 'VP of Sales',
      company: 'TechCorp Solutions',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=64&h=64&fit=crop&crop=face',
      quote: `LeadGen Pro transformed our sales process. We've increased our qualified leads by 300% in just 3 months.`,
      rating: 5
    },
    {
      id: 2,
      name: 'Michael Chen',role: 'Marketing Director',company: 'DataSoft Inc',avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=64&h=64&fit=crop&crop=face',
      quote: `The AI-powered lead scoring is incredibly accurate. Our conversion rates have doubled since switching to LeadGen Pro.`,
      rating: 5
    },
    {
      id: 3,
      name: 'Jennifer Martinez',role: 'Business Owner',company: 'CloudTech Ventures',avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=64&h=64&fit=crop&crop=face',
      quote: `Easy to use, powerful features, and excellent support. LeadGen Pro is essential for our growth strategy.`,
      rating: 5
    }
  ];

  const certifications = [
    {
      name: 'GDPR',
      description: 'General Data Protection Regulation Compliant',
      icon: 'Shield'
    },
    {
      name: 'CCPA',
      description: 'California Consumer Privacy Act Compliant',
      icon: 'Lock'
    },
    {
      name: 'SOC 2',
      description: 'Service Organization Control 2 Certified',
      icon: 'CheckCircle'
    },
    {
      name: 'ISO 27001',
      description: 'Information Security Management Certified',
      icon: 'Award'
    }
  ];

  const stats = [
    {
      value: '50,000+',
      label: 'Active Users',
      icon: 'Users'
    },
    {
      value: '10M+',
      label: 'Leads Generated',
      icon: 'Target'
    },
    {
      value: '99.9%',
      label: 'Uptime',
      icon: 'Activity'
    },
    {
      value: '4.9/5',
      label: 'Customer Rating',
      icon: 'Star'
    }
  ];

  return (
    <div className="space-y-8">
      {/* Security Features */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4 text-center">
          Enterprise-Grade Security & Compliance
        </h3>
        <div className="grid grid-cols-1 gap-4">
          {securityFeatures?.map((feature, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
              <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center flex-shrink-0">
                <Icon name={feature?.icon} size={16} className="text-primary" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground">
                  {feature?.title}
                </h4>
                <p className="text-xs text-muted-foreground">
                  {feature?.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Statistics */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-foreground mb-4 text-center">
          Trusted by Thousands of Businesses
        </h3>
        <div className="grid grid-cols-2 gap-4">
          {stats?.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Icon name={stat?.icon} size={16} className="text-primary" />
              </div>
              <div className="text-lg font-bold text-foreground">
                {stat?.value}
              </div>
              <div className="text-xs text-muted-foreground">
                {stat?.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Customer Testimonials */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4 text-center">
          What Our Customers Say
        </h3>
        <div className="space-y-4">
          {testimonials?.map((testimonial) => (
            <div key={testimonial?.id} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center space-x-1 mb-3">
                {[...Array(testimonial?.rating)]?.map((_, i) => (
                  <Icon key={i} name="Star" size={12} className="text-warning fill-current" />
                ))}
              </div>
              <blockquote className="text-sm text-muted-foreground mb-3">
                "{testimonial?.quote}"
              </blockquote>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                  <Image
                    src={testimonial?.avatar}
                    alt={testimonial?.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">
                    {testimonial?.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {testimonial?.role}, {testimonial?.company}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Certifications */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 text-center">
          Certifications & Compliance
        </h3>
        <div className="grid grid-cols-1 gap-3">
          {certifications?.map((cert, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
              <div className="w-6 h-6 bg-primary/10 rounded flex items-center justify-center flex-shrink-0">
                <Icon name={cert?.icon} size={14} className="text-primary" />
              </div>
              <div>
                <div className="text-sm font-medium text-foreground">
                  {cert?.name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {cert?.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Money Back Guarantee */}
      <div className="bg-success/5 border border-success/20 rounded-lg p-4 text-center">
        <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <Icon name="Shield" size={24} className="text-success" />
        </div>
        <h3 className="text-base font-semibold text-foreground mb-2">
          30-Day Money-Back Guarantee
        </h3>
        <p className="text-sm text-muted-foreground">
          Not satisfied? Get a full refund within 30 days, no questions asked.
        </p>
      </div>
    </div>
  );
};

export default TrustSignals;